import { countryConfig } from '../country';

/**
 * "Did you mean gmail.com?" — an email domain-typo suggester for the signup/login
 * field (TZ 2026-08-28, §2). ~7% of the OTP drop is mail sent to an address that
 * can never receive it: `gmali.com`, `gnail.com`, `iclou.com`, `yandex.con`…
 *
 * Follows the mailcheck.js approach (Kicksend — ~50% fewer confirmation-email
 * bounces): rather than one edit-distance over the whole domain, split it into a
 * second-level part (`gmail`) and a top-level part (`com`) and correct each
 * against its own list. That catches TLD typos — `.con`/`.cmo`/`.coom` → `.com`
 * — which a single full-string distance misses, while a legitimate TLD (it's in
 * the list) matches itself at distance 0 and is never "corrected". Corporate and
 * `*.uz` addresses pass through untouched: their second-level part is near no
 * known provider, and their TLD is a valid one.
 *
 * Suggest-only, never blocking.
 */

/** Spain's own consumer and country domains — present on the ES deployment
 *  only, so the UZ/KZ lists (and suggestions) stay exactly what they were. A
 *  `.es` address must never be «corrected» to a nearer TLD. */
const IS_ES = countryConfig.code === 'es';
const ES_DOMAINS: readonly string[] = IS_ES ? ['hotmail.es', 'outlook.es', 'yahoo.es'] : [];
const ES_TOP_LEVEL: readonly string[] = IS_ES ? ['es', 'eu', 'cat'] : [];
/** Chile (Liciora): Microsoft's Chilean consumer domain and the `.cl` TLD — no
 *  Spanish ISP or `.es` list there. Absent on every other deployment. */
const IS_CL = countryConfig.code === 'cl';
const CL_DOMAINS: readonly string[] = IS_CL ? ['hotmail.cl'] : [];
const CL_TOP_LEVEL: readonly string[] = IS_CL ? ['cl'] : [];

/** Consumer domains our users actually typo toward (from the prod data). */
const BASE_DOMAINS = [
  'gmail.com',
  'mail.ru',
  'yandex.ru',
  'icloud.com',
  'bk.ru',
  'inbox.ru',
  'list.ru',
  'outlook.com',
  'hotmail.com',
  'yahoo.com',
] as const;
const COMMON_DOMAINS: readonly string[] = [...BASE_DOMAINS, ...ES_DOMAINS, ...CL_DOMAINS];

/** Provider second-level names — the part before the final dot. */
const SECOND_LEVEL = [
  'gmail',
  'yandex',
  'mail',
  'icloud',
  'outlook',
  'hotmail',
  'yahoo',
  'bk',
  'inbox',
  'list',
  'me',
  'mac',
];

/**
 * Top-level parts. This list's job is TWO-fold: propose a correction for an
 * invalid TLD, AND — because a valid TLD in here matches itself at distance 0 —
 * protect legitimate ones (`io`, `dev`, `uz`, `kz`…) from being "corrected" to a
 * nearer neighbour. So it must carry the TLDs our users legitimately use, not
 * only the common ones.
 */
const TOP_LEVEL = [
  'com',
  'ru',
  'uz',
  'org',
  'net',
  'io',
  'co',
  'info',
  'biz',
  'me',
  'dev',
  'app',
  'kz',
  'tj',
  'kg',
  'az',
  'ge',
  'tm',
  'edu',
  'gov',
  'ai',
  'tech',
  'online',
  'uk',
  ...ES_TOP_LEVEL,
  ...CL_TOP_LEVEL,
];

/** Levenshtein edit distance, abandoned early once it exceeds `max`. */
function editDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      cur[j] = v;
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > max) return max + 1;
    prev = cur;
  }
  return prev[b.length];
}

/** The closest candidate within `max` edits, or null. Distance 0 (an exact hit)
 *  returns the value itself, so a legitimate part is preserved, not replaced. */
function closest(value: string, candidates: readonly string[], max: number): string | null {
  let best: { v: string; d: number } | null = null;
  for (const c of candidates) {
    const d = editDistance(value, c, max);
    if (d <= max && (best === null || d < best.d)) best = { v: c, d };
  }
  return best ? best.v : null;
}

/**
 * The corrected email if the domain looks like a typo of a known one, else null.
 * Returns null for an already-valid domain, an incomplete address, or anything
 * whose parts are all near-nothing (corp / `*.uz`).
 */
export function suggestEmail(raw: string): string | null {
  const email = raw.trim().toLowerCase();
  const at = email.lastIndexOf('@');
  if (at < 1 || at === email.length - 1) return null; // no local part or no domain
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const dot = domain.indexOf('.');
  if (dot < 1 || dot === domain.length - 1) return null; // still typing the domain
  if (COMMON_DOMAINS.includes(domain)) return null; // already correct

  const sld = domain.slice(0, dot);
  const tld = domain.slice(dot + 1);

  // A) Nearest KNOWN domain. This resolves short-TLD ambiguity the split can't:
  //    `hotmail.cmo` is 1 edit from `hotmail.co` but 2 from `hotmail.com`, yet
  //    `hotmail.com` is the real domain and wins here. Guard: reject a match that
  //    swaps the provider WHEN the typed provider is already valid — that is the
  //    line between `gmali.com → gmail.com` (gmali is a typo, fix it) and
  //    `gmail.ru → mail.ru` (gmail is a real provider, never rewrite it to mail).
  const bestFull = closest(domain, COMMON_DOMAINS, 2);
  if (bestFull) {
    const swapsValidProvider =
      bestFull.slice(0, bestFull.indexOf('.')) !== sld && SECOND_LEVEL.includes(sld);
    if (!swapsValidProvider) return `${local}@${bestFull}`;
  }

  // B) No known domain is near, but the TLD alone is invalid — fix only that and
  //    keep the typed provider (`yandex.con` → `yandex.com`; yandex.com is not in
  //    the common list, so A can't reach it). `umail.uz` survives here: `uz` is a
  //    valid TLD, so nothing changes.
  const newTld = closest(tld, TOP_LEVEL, 2) ?? tld;
  if (newTld !== tld) return `${local}@${sld}.${newTld}`;

  return null;
}

/* ── the checkout half ──────────────────────────────────────────────────────
 *
 * `suggestEmail` above answers "this address is WELL-FORMED but looks like a
 * typo" — the signup screen's question, asked after `isLikelyEmail` has already
 * passed. Its very first guard (`dot < 1` → "still typing the domain") makes it
 * deliberately silent on an address with NO dot in the domain, because on a
 * live-typing field that is someone mid-keystroke, not someone who is wrong.
 *
 * The payment sheet asks the opposite question, and only ever after a press:
 * the address has ALREADY been refused by `isLikelyEmail`, so a missing dot is
 * final rather than provisional. 2026-09-20: a guest in the Instagram in-app
 * browser at 360×365 with the keyboard up tapped «Продолжить на Octobank» 24
 * times in three minutes with exactly that address, saw the same restated rule
 * each time, and left.
 *
 * So the functions below are ADDITIVE and the signup path is untouched — they
 * are reached only from a refused address, where "did you mean" cannot
 * interrupt anyone whose address works.
 */

/**
 * Providers a bare domain can be completed to — `gmail` → `gmail.com`.
 *
 * Read from `COMMON_DOMAINS`, so the two can never disagree, and FIRST MATCH
 * WINS: `mail` completes to `mail.ru` rather than any later `mail.*`, which is
 * the commoner of the two among our buyers.
 */
function completeProvider(provider: string): string | null {
  return COMMON_DOMAINS.find((d) => d.slice(0, d.indexOf('.')) === provider) ?? null;
}

/** Is this domain shaped the way the API's `IsEmail` demands — a dot, then a
 *  TLD of two or more letters? Mirrors `EMAIL_SHAPE` in `auth-error.ts`; the
 *  functions below apply only when it is NOT. */
function domainLooksComplete(domain: string): boolean {
  const dot = domain.lastIndexOf('.');
  return dot >= 1 && /^[a-z]{2,}$/.test(domain.slice(dot + 1));
}

/**
 * The corrected address for one the server has ALREADY refused, or null.
 *
 * Three routes, in order of confidence:
 *   1. `suggestEmail` — a well-formed domain that is a known typo. Rare here
 *      (most of those pass `isLikelyEmail` and never reach this) but free.
 *   2. The whole domain against the known list — catches a DROPPED DOT, where
 *      every letter is still there (`gmailcom` → `gmail.com`, one edit).
 *   3. The provider name alone, within one edit — catches a domain typed with
 *      no TLD at all (`gmail` → `gmail.com`, `gmial` → `gmail.com`).
 *
 * A corporate domain reaches none of them: `corp` is ≥4 edits from every
 * provider and every known domain, so a B2B buyer is never asked whether they
 * meant gmail.
 */
export function suggestRefusedEmail(raw: string): string | null {
  const known = suggestEmail(raw);
  if (known) return known;

  const email = raw.trim().toLowerCase();
  const at = email.lastIndexOf('@');
  if (at < 1 || at === email.length - 1) return null;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  // Shaped fine → not this function's case, whatever else is wrong with it.
  if (domainLooksComplete(domain)) return null;

  const whole = closest(domain, COMMON_DOMAINS, 2);
  if (whole) return `${local}@${whole}`;

  const dot = domain.lastIndexOf('.');
  const sld = dot < 0 ? domain : domain.slice(0, dot);
  if (!sld) return null;
  /*
   * Two edits only for a name long enough to survive them. `gmial` → `gmail`
   * is a transposition, which plain Levenshtein charges 2 for, so a flat max of
   * 1 misses the commonest typo of the commonest provider. But at 2 a SHORT
   * corporate name lands on a short provider by accident — `bank` is 2 from
   * `bk`, and «вы имели в виду a@bk.ru?» under a bank's own address is worse
   * than saying nothing. The length gate keeps the bold radius for the long
   * names where it is earned.
   */
  const provider = closest(sld, SECOND_LEVEL, sld.length >= 5 ? 2 : 1);
  const completed = provider ? completeProvider(provider) : null;
  return completed ? `${local}@${completed}` : null;
}

/**
 * A privacy-safe label for WHY an address was refused, for the
 * `login_failed{reason:'validation_error', client:true}` meta.
 *
 * ⚠️ Never the local part, and never the RAW domain. `typo:<domain>` names a
 * domain from `COMMON_DOMAINS` — a constant of ours, not anything the user
 * typed — so the row says "we recognised a gmail typo", not whose address it
 * was. Same rule `reg_otp_requested`'s `domain` key already follows.
 *
 * `typo:` wins over the structural codes when there is a suggestion, because it
 * is the more actionable fact: it says we understood the mistake AND offered
 * the fix, which is the number that decides whether the offer earns its place.
 * The structural codes are what is left when we did not.
 */
export function emailDomainShape(raw: string): string {
  const email = raw.trim().toLowerCase();
  const at = email.lastIndexOf('@');
  if (at < 1 || at === email.length - 1) return 'no_at';
  const domain = email.slice(at + 1);

  const suggestion = suggestRefusedEmail(email);
  if (suggestion) {
    const suggested = suggestion.slice(suggestion.lastIndexOf('@') + 1);
    // Only a domain we own the string for may be named. A TLD-only repair
    // (route B of `suggestEmail`) keeps the buyer's own second level, so it is
    // reported structurally rather than printed into an event.
    if (COMMON_DOMAINS.includes(suggested)) return `typo:${suggested}`;
  }

  const dot = domain.lastIndexOf('.');
  if (dot < 1) return 'no_dot';
  if (!/^[a-z]{2,}$/.test(domain.slice(dot + 1))) return 'no_tld';
  return 'other';
}
